/**
 * AISetupModal Component
 *
 * Modal for the AI Application Setup feature. Provides a two-step flow:
 * 1. User enters application name and code, clicks [Validate]
 * 2. If validation passes, user clicks [Create Everything]
 *
 * The setup creates an application, deployment token, GitHub repository,
 * configures repo variables/secrets, and pushes template content.
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { useTemplates, useAISetupValidate, useAISetup } from '../hooks/useAISetup';
import { useAuth } from '../../../contexts/AuthContext';
import { getErrorMessage } from '../../../lib/utils/errors';
import type { AISetupValidateResponse } from '../../../types/ai-setup.types';

interface AISetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * Generate application code from name
 * Same pattern as CreateApplicationModal
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

/**
 * Step indicator for completed setup steps
 */
const STEP_LABELS: Record<string, string> = {
  application_created: 'Application created',
  deployment_token_created: 'Deployment token created',
  repository_created: 'GitHub repository created',
  repository_configured: 'Repository configured',
  template_pushed: 'Template pushed',
};

export const AISetupModal: React.FC<AISetupModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const { tenant } = useAuth();

  // Form state
  const [applicationName, setApplicationName] = useState('');
  const [applicationCode, setApplicationCode] = useState('');
  const [autoGenerateCode, setAutoGenerateCode] = useState(true);
  const [description, setDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('HelloWorld');

  // Validation state
  const [validationResult, setValidationResult] = useState<AISetupValidateResponse | null>(null);
  const [isValidated, setIsValidated] = useState(false);

  // Setup result state
  const [setupComplete, setSetupComplete] = useState(false);
  const [repoUrl, setRepoUrl] = useState('');

  // Fetch templates
  const templatesQuery = useTemplates({ tenantId, enabled: isOpen });

  const templateOptions = (templatesQuery.data || []).map((t) => ({
    value: t.name,
    label: t.name,
  }));

  const selectedTemplateInfo = templatesQuery.data?.find(
    (t) => t.name === selectedTemplate
  );

  // Derive repository prefix and org from tenant settings
  const githubOrg = (tenant?.settings?.githubOrganization as string) || '';
  const repoPrefix = (tenant?.settings?.repositoryPrefix as string) || '';
  const repoName = `${repoPrefix}${applicationCode}`;

  // Auto-generate code from name
  useEffect(() => {
    if (autoGenerateCode && applicationName) {
      setApplicationCode(generateCodeFromName(applicationName));
    }
  }, [applicationName, autoGenerateCode]);

  // Reset validation when inputs change
  useEffect(() => {
    setValidationResult(null);
    setIsValidated(false);
  }, [applicationCode]);

  // Mutations
  const validateMutation = useAISetupValidate({
    tenantId,
    onSuccess: (data) => {
      setValidationResult(data);
      setIsValidated(data.canProceed);
    },
  });

  const setupMutation = useAISetup({
    tenantId,
    onSuccess: (data) => {
      setSetupComplete(true);
      setRepoUrl(data.repository.url);
    },
  });

  const handleClose = () => {
    setApplicationName('');
    setApplicationCode('');
    setAutoGenerateCode(true);
    setDescription('');
    setSelectedTemplate('HelloWorld');
    setValidationResult(null);
    setIsValidated(false);
    setSetupComplete(false);
    setRepoUrl('');
    validateMutation.reset();
    setupMutation.reset();
    onClose();
  };

  const handleValidate = () => {
    if (!applicationCode.trim()) return;
    validateMutation.mutate({ applicationCode: applicationCode.trim() });
  };

  const handleCreate = () => {
    if (!isValidated || !applicationName.trim() || !applicationCode.trim()) return;
    // Clear any previous error before retrying
    setupMutation.reset();
    setupMutation.mutate({
      applicationName: applicationName.trim(),
      applicationCode: applicationCode.trim(),
      description: description.trim() || undefined,
      template: selectedTemplate,
      repositoryVisibility: 'private',
      actions: ['read', 'write', 'delete'],
    });
  };

  const isValidating = validateMutation.isPending;
  const isCreating = setupMutation.isPending;
  const isLoading = isValidating || isCreating;

  // Check if GitHub integration is configured
  const isGitHubConfigured = !!githubOrg && !!repoPrefix;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        AI Application Setup
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* GitHub not configured warning */}
          {!isGitHubConfigured && (
            <Alert variant="warning">
              GitHub integration is not configured. Please go to Settings &gt; GitHub Integration to set up your organization and repository prefix before using this feature.
            </Alert>
          )}

          {/* Setup complete state */}
          {setupComplete && (
            <div className="space-y-4">
              <Alert variant="success">
                Application setup completed successfully!
              </Alert>

              {/* Completed steps */}
              <div className="border rounded-lg p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
                <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
                  Completed Steps
                </h4>
                <ul className="space-y-2">
                  {setupMutation.data?.completedSteps.map((step) => (
                    <li key={step} className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {STEP_LABELS[step] || step}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Repository link */}
              {repoUrl && (
                <div className="border rounded-lg p-4" style={{ borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Repository:
                  </p>
                  <a
                    href={repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 underline"
                  >
                    {repoUrl}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Setup form (hidden when complete) */}
          {!setupComplete && (
            <>
              {/* Error alerts */}
              {validateMutation.error && (
                <Alert variant="error">
                  {getErrorMessage(validateMutation.error)}
                </Alert>
              )}
              {setupMutation.error && (
                <Alert variant="error">
                  {getErrorMessage(setupMutation.error)}
                  {' '}You can safely click &quot;Create Everything&quot; again to retry — already completed steps will be skipped.
                </Alert>
              )}

              {/* Application Name */}
              <div>
                <Input
                  label="Application Name"
                  placeholder="My Application"
                  value={applicationName}
                  onChange={(e) => setApplicationName(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Application Code */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                    Application Code <span className="text-red-500">*</span>
                  </label>
                  <label className="flex items-center text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={autoGenerateCode}
                      onChange={(e) => setAutoGenerateCode(e.target.checked)}
                      className="mr-1.5 h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      disabled={isLoading}
                    />
                    Auto-generate from name
                  </label>
                </div>
                <Input
                  placeholder="my-application"
                  value={applicationCode}
                  onChange={(e) => {
                    setAutoGenerateCode(false);
                    setApplicationCode(e.target.value);
                  }}
                  disabled={isLoading || autoGenerateCode}
                />
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Used as the application identifier and part of the repository name.
                </p>
              </div>

              {/* Repository name preview */}
              {applicationCode && isGitHubConfigured && (
                <div className="border rounded-lg p-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>
                    Repository will be created as:
                  </p>
                  <p className="text-sm font-mono" style={{ color: 'var(--color-text-primary)' }}>
                    {githubOrg}/{repoName}
                  </p>
                </div>
              )}

              {/* Validation results */}
              {validationResult && (
                <div className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center gap-2 text-sm">
                    {validationResult.applicationCode.valid ? (
                      <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span style={{ color: validationResult.applicationCode.valid ? 'var(--color-text-secondary)' : 'var(--color-danger)' }}>
                      Application code: {validationResult.applicationCode.valid ? 'Available' : validationResult.applicationCode.reason}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {validationResult.repository.valid ? (
                      <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span style={{ color: validationResult.repository.valid ? 'var(--color-text-secondary)' : 'var(--color-danger)' }}>
                      Repository ({validationResult.repository.name}): {validationResult.repository.valid ? 'Available' : validationResult.repository.reason}
                    </span>
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <Textarea
                  label="Description"
                  placeholder="A brief description of your application (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isLoading}
                  rows={2}
                />
              </div>

              {/* Template selection */}
              <div>
                <Select
                  label="Template"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  options={templateOptions.length > 0 ? templateOptions : [{ value: 'HelloWorld', label: 'HelloWorld' }]}
                  disabled={isLoading || templatesQuery.isLoading}
                  helperText={selectedTemplateInfo?.description}
                />
              </div>

              {/* What will be created */}
              <div className="border rounded-lg p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
                <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  This will create:
                </h4>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <li>- A Ketrics application with the {selectedTemplate} template</li>
                  <li>- A 365-day CI/CD deployment token</li>
                  <li>- A GitHub repository with template code pushed</li>
                  <li>- GitHub Actions variables and secrets configured</li>
                </ul>
              </div>

              {/* Creating progress */}
              {isCreating && setupMutation.data?.completedSteps && (
                <div className="border rounded-lg p-3" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Spinner size="sm" />
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      Setting up...
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        {setupComplete ? (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>

            {/* Validate button */}
            {!isValidated && (
              <Button
                variant="secondary"
                onClick={handleValidate}
                loading={isValidating}
                disabled={isLoading || !applicationCode.trim() || !isGitHubConfigured}
              >
                Validate
              </Button>
            )}

            {/* Create button - only enabled after validation passes */}
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={isCreating}
              disabled={isLoading || !isValidated || !applicationName.trim()}
            >
              Create Everything
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
